// Copyright 2026 FlairCV Contributors
// SPDX-License-Identifier: AGPL-3.0-or-later

import { z } from 'zod';

/**
 * Strips bullet-point markers, list prefixes, and surrounding whitespace
 * from a detail line, keeping only the clean text.
 *
 * Handles: Unicode bullets, ASCII dashes/asterisks, en/em dashes,
 * numbered prefixes like "1.", "2)", "(3)".
 */
const leadingBulletRe = /^[\s]*(?:[•◦▪▸►‣⁃◆◇○●★☆→⇒»\-*–—]+|\(?[0-9]+[.)]+)[\s]*/;

export function cleanDetailContent(s: string): string {
  return s.trim().replace(leadingBulletRe, '').trim();
}

/** Schema for creating/editing a single experience detail line. */
export const experienceDetailSchema = z.object({
  content: z
    .string()
    .min(1, 'Detail content is required.')
    .max(5000, 'Detail content must be at most 5,000 characters.'),
});

export type ExperienceDetailFormData = z.infer<typeof experienceDetailSchema>;

/** Schema for bulk-adding multiple detail lines (one per line in a textarea). */
export const bulkExperienceDetailSchema = z.object({
  content: z
    .string()
    .min(1, 'At least one line is required.'),
});

export type BulkExperienceDetailFormData = z.infer<typeof bulkExperienceDetailSchema>;
